import { useState, useReducer, useEffect, useCallback } from 'react'

import { useSearchParams } from 'react-router-dom'

import { useDebouncedCallback } from '@bettermode/common/hooks'
import { EmailStatus, EmailType } from '@tribeplatform/gql-client/types'
import { useI18n, useStaticIntl } from '@tribeplatform/react-components/i18n'
import { useEmailLogs } from '@tribeplatform/react-sdk/hooks'
import { Container } from '@tribeplatform/react-ui-kit/Layout'

import { Table } from '../../Table/index.js'

import {
  EMAIL_LOGS_PAGE_SIZE,
  emailLogsColumnsFactory,
  emailStatusFactory,
  emailStatusMappingFactory,
  emailTypesFactory,
} from './constants.js'
import { EmailLogsFilterBar } from './EmailLogsFilterBar.js'
import { EmailStatusModal } from './EmailStatusModal.js'

// Filter state & reducer
type FilterOptions = {
  type: EmailType | undefined
  status: EmailStatus | undefined
  recipient: string | undefined
}

type FiltersAction =
  | { type: 'SET_TYPE'; payload: EmailType | undefined }
  | { type: 'SET_STATUS'; payload: EmailStatus | undefined }
  | { type: 'SET_RECIPIENT'; payload: string | undefined }

function createFiltersInitialState(
  searchParams: URLSearchParams,
): FilterOptions {
  return {
    type: (searchParams.get('type') as EmailType) || undefined,
    status: (searchParams.get('status') as EmailStatus) || undefined,
    recipient: searchParams.get('recipient') || undefined,
  }
}

function filtersReducer(
  state: FilterOptions,
  action: FiltersAction,
): FilterOptions {
  /**
   * Normalize empty payloads into undefined
   */
  const payload = !action.payload ? undefined : action.payload

  switch (action.type) {
    case 'SET_TYPE':
      return { ...state, type: payload as EmailType }
    case 'SET_STATUS':
      return { ...state, status: payload as EmailStatus }
    case 'SET_RECIPIENT':
      return { ...state, recipient: payload as string }
    default:
      return state
  }
}

export const EmailLogs = () => {
  const { $t } = useI18n()
  const [emailStatusModal, setEmailStatusModal] = useState(false)
  const emailStatusMapping = useStaticIntl(emailStatusMappingFactory)
  const emailStatusOptions = useStaticIntl(
    emailStatusFactory,
    emailStatusMapping,
  )
  const emailTypeOptions = useStaticIntl(emailTypesFactory)
  const columns = useStaticIntl(emailLogsColumnsFactory, emailTypeOptions)

  const [searchParams, setSearchParams] = useSearchParams()

  const [filters, dispatchFilters] = useReducer(
    filtersReducer,
    searchParams,
    createFiltersInitialState,
  )

  const handleRecipientChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatchFilters({
        type: 'SET_RECIPIENT',
        payload: e?.target?.value,
      })
    },
    [dispatchFilters],
  )

  const handleRecipientChangeDebounced = useDebouncedCallback(
    handleRecipientChange,
    500,
  )

  useEffect(() => {
    const params = Object.fromEntries(
      Object.entries({
        type: filters.type,
        status: filters.status,
        recipient: filters.recipient,
      }).filter(([_, value]) => value !== undefined),
    )
    setSearchParams(params, {
      preventScrollReset: true,
    })
  }, [filters.recipient, filters.status, filters.type, setSearchParams])

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useEmailLogs({
      variables: {
        limit: EMAIL_LOGS_PAGE_SIZE,
        types: filters.type ? [filters.type] : undefined,
        statuses: filters.status ? [filters.status] : undefined,
        recipient: filters.recipient,
        reverse: true,
      },
      useInfiniteQueryOptions: { keepPreviousData: false },
    })

  const isLoadingInitialPage = isLoading && !isFetchingNextPage

  return (
    <Container size="full">
      <Table
        entityType="emailLog"
        hasSearch={false}
        isLoading={isLoadingInitialPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        columns={columns}
        data={data}
        title={$t({ defaultMessage: 'Email logs', id: 'Generics.EmailLogs' })}
        filterBarPrefix={
          <EmailLogsFilterBar
            filters={filters}
            dispatchFilters={dispatchFilters}
            emailTypeOptions={emailTypeOptions}
            emailStatusOptions={emailStatusOptions}
            setEmailStatusModal={setEmailStatusModal}
            handleRecipientChange={handleRecipientChangeDebounced}
          />
        }
        keyboardControl
      />
      {emailStatusModal && (
        <EmailStatusModal
          open={emailStatusModal}
          onClose={() => setEmailStatusModal(false)}
        />
      )}
    </Container>
  )
}
